import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGroups } from '../../contexts/GroupContext';
import { Group } from '../../contexts/GroupContext';

const ContactManagement = () => {
  const navigate = useNavigate();
  const { groups, updateGroup, deleteGroup } = useGroups();
  const [selectedFormat, setSelectedFormat] = useState('csv');
  const [selectedGroup, setSelectedGroup] = useState('all');
  
  // Import modal states
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importGroup, setImportGroup] = useState('all');
  const [importFormat, setImportFormat] = useState('csv');
  const [importOptions, setImportOptions] = useState({
    skipDuplicates: true,
    validateEmails: true,
    validatePhones: true
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Export modal states
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportGroup, setExportGroup] = useState('all');
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportOptions, setExportOptions] = useState({
    includeHeaders: true,
    includeMetadata: true,
    includeTags: true
  });

  // Edit modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    type: '',
    settings: {
      allowDuplicates: false,
      autoValidate: true,
      requireEmail: false,
      requirePhone: false
    }
  });

  // Delete confirmation modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImportFile(e.target.files[0]);
    }
  };

  // Open import modal
  const openImportModal = () => {
    setIsImportModalOpen(true);
  };

  // Close import modal
  const closeImportModal = () => {
    setIsImportModalOpen(false);
    setImportFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Open export modal
  const openExportModal = () => {
    setIsExportModalOpen(true);
  };

  // Close export modal
  const closeExportModal = () => {
    setIsExportModalOpen(false);
  };

  // Navigate to create group page
  const navigateToCreateGroup = () => {
    navigate('/superadmin/create-group');
  };

  // Handle import submission
  const handleImportSubmit = () => {
    if (!importFile) {
      alert('Please select a file to import');
      return;
    }
    
    // In a real application, this would handle the file upload and processing
    console.log('Importing file:', importFile.name);
    console.log('Import format:', importFormat);
    console.log('Import group:', importGroup);
    console.log('Import options:', importOptions);
    
    // Simulate successful import
    alert(`Successfully imported ${importFile.name} to ${importGroup} group`);
    closeImportModal();
  };

  // Handle export submission
  const handleExportSubmit = () => {
    // In a real application, this would generate and download the file
    console.log('Exporting contacts from group:', exportGroup);
    console.log('Export format:', exportFormat);
    console.log('Export options:', exportOptions);
    
    // Simulate successful export
    alert(`Successfully exported contacts from ${exportGroup} group in ${exportFormat.toUpperCase()} format`);
    closeExportModal();
  };

  // Toggle import option
  const toggleImportOption = (option: string) => {
    setImportOptions(prev => ({
      ...prev,
      [option]: !prev[option as keyof typeof prev]
    }));
  };

  // Toggle export option
  const toggleExportOption = (option: string) => {
    setExportOptions(prev => ({
      ...prev,
      [option]: !prev[option as keyof typeof prev]
    }));
  };

  // Open edit modal
  const openEditModal = (group: Group) => {
    setEditingGroup(group);
    setEditForm({
      name: group.name,
      description: group.description,
      type: group.type,
      settings: { ...group.settings }
    });
    setIsEditModalOpen(true);
  };

  // Close edit modal
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingGroup(null);
  };

  // Handle edit form submission
  const handleEditSubmit = () => {
    if (editingGroup) {
      updateGroup(editingGroup.id, editForm);
      closeEditModal();
    }
  };

  // Handle edit form changes
  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setEditForm(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          [name]: checkbox.checked
        }
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (group: Group) => {
    setGroupToDelete(group);
    setIsDeleteModalOpen(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setGroupToDelete(null);
  };

  // Handle group deletion
  const handleDeleteGroup = () => {
    if (groupToDelete) {
      deleteGroup(groupToDelete.id);
      closeDeleteModal();
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-medium mb-6">Contact Management</h1>

      {/* Section 1: Import and Export Contacts */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">Import and Export Contacts</h2>
          <div className="flex space-x-3">
            <button 
              className="border-2 border-[#FF5722] text-[#FF5722] px-4 py-2 rounded-md hover:bg-[#FF5722] hover:text-white transition-colors"
              onClick={openExportModal}
            >
              Export Contacts
            </button>
            <button 
              className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
              onClick={openImportModal}
            >
              Import Contacts
            </button>
          </div>
        </div>

        {/* Import/Export Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Total Contacts</h3>
            <div className="text-2xl font-bold text-[#FF5722]">25,678</div>
            <p className="text-sm text-gray-600">Across all groups</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Last Import</h3>
            <div className="text-2xl font-bold text-blue-600">1,234</div>
            <p className="text-sm text-gray-600">2 days ago</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Last Export</h3>
            <div className="text-2xl font-bold text-green-600">5,432</div>
            <p className="text-sm text-gray-600">Yesterday</p>
          </div>
        </div>

        {/* Recent Import/Export History */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Operation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">File Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Records</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">Import</td>
                <td className="px-6 py-4 whitespace-nowrap">contacts_batch1.csv</td>
                <td className="px-6 py-4 whitespace-nowrap">1,234</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    Completed
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">2023-10-15</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 2: Create and Manage Groups */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">Group Management</h2>
          <button 
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
            onClick={navigateToCreateGroup}
          >
            Create New Group
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Group List */}
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-4">Contact Groups</h3>
            <div className="space-y-3">
              {groups.map((group) => (
                <div key={group.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <p className="font-medium">{group.name}</p>
                    <p className="text-sm text-gray-600">{group.contactCount} contacts</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => openEditModal(group)}
                    >
                      Edit
                    </button>
                    <button 
                      className="text-red-600 hover:text-red-800"
                      onClick={() => openDeleteModal(group)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Group Analytics */}
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-4">Group Analytics</h3>
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded">
                <p className="font-medium">Most Active Group</p>
                <p className="text-sm text-gray-600">VIP Customers</p>
                <div className="mt-2 h-2 bg-gray-200 rounded-full">
                  <div className="h-2 bg-[#FF5722] rounded-full" style={{ width: '85%' }}></div>
                </div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <p className="font-medium">Growth Rate</p>
                <p className="text-sm text-gray-600">+15% this month</p>
                <div className="mt-2 h-2 bg-gray-200 rounded-full">
                  <div className="h-2 bg-green-500 rounded-full" style={{ width: '65%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Clean and Validate Contacts */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">Contact Validation</h2>
          <button 
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
            onClick={() => navigate('/superadmin/contact-validation')}
          >
            Start Validation
          </button>
        </div>

        {/* Validation Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Valid</h3>
            <div className="text-2xl font-bold text-green-600">23,456</div>
            <p className="text-sm text-gray-600">91.3%</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Invalid</h3>
            <div className="text-2xl font-bold text-red-600">1,234</div>
            <p className="text-sm text-gray-600">4.8%</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Duplicates</h3>
            <div className="text-2xl font-bold text-yellow-600">988</div>
            <p className="text-sm text-gray-600">3.9%</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Last Cleaned</h3>
            <div className="text-sm font-bold">2 days ago</div>
            <p className="text-sm text-gray-600">Auto-clean enabled</p>
          </div>
        </div>

        {/* Validation Settings */}
        <div className="border rounded-lg p-4">
          <h3 className="font-medium mb-4">Validation Settings</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Auto-Clean</p>
                <p className="text-sm text-gray-600">Automatically clean contacts weekly</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" checked />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
              </label>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Duplicate Check</p>
                <p className="text-sm text-gray-600">Check for duplicate contacts</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" checked />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Import Contacts Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-medium">Import Contacts</h2>
                <button 
                  onClick={closeImportModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* File Upload Section */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Contact File
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-[#FF5722] hover:text-[#FF5722]/80 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#FF5722]">
                        <span>Upload a file</span>
                        <input 
                          id="file-upload" 
                          name="file-upload" 
                          type="file" 
                          className="sr-only" 
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          accept=".csv,.xlsx,.xls,.txt"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      CSV, Excel, or TXT up to 10MB
                    </p>
                  </div>
                </div>
                {importFile && (
                  <div className="mt-2 text-sm text-gray-600">
                    Selected file: <span className="font-medium">{importFile.name}</span>
                  </div>
                )}
              </div>

              {/* Import Options */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Import Options</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      File Format
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                      value={importFormat}
                      onChange={(e) => setImportFormat(e.target.value)}
                    >
                      <option value="csv">CSV</option>
                      <option value="excel">Excel</option>
                      <option value="txt">TXT</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Import to Group
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                      value={importGroup}
                      onChange={(e) => setImportGroup(e.target.value)}
                    >
                      <option value="all">All Contacts</option>
                      {groups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      id="skip-duplicates"
                      type="checkbox"
                      className="h-4 w-4 text-[#FF5722] focus:ring-[#FF5722] border-gray-300 rounded"
                      checked={importOptions.skipDuplicates}
                      onChange={() => toggleImportOption('skipDuplicates')}
                    />
                    <label htmlFor="skip-duplicates" className="ml-2 block text-sm text-gray-700">
                      Skip duplicate contacts
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="validate-emails"
                      type="checkbox"
                      className="h-4 w-4 text-[#FF5722] focus:ring-[#FF5722] border-gray-300 rounded"
                      checked={importOptions.validateEmails}
                      onChange={() => toggleImportOption('validateEmails')}
                    />
                    <label htmlFor="validate-emails" className="ml-2 block text-sm text-gray-700">
                      Validate email addresses
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="validate-phones"
                      type="checkbox"
                      className="h-4 w-4 text-[#FF5722] focus:ring-[#FF5722] border-gray-300 rounded"
                      checked={importOptions.validatePhones}
                      onChange={() => toggleImportOption('validatePhones')}
                    />
                    <label htmlFor="validate-phones" className="ml-2 block text-sm text-gray-700">
                      Validate phone numbers
                    </label>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={closeImportModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors"
                  onClick={handleImportSubmit}
                >
                  Import Contacts
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Contacts Modal */}
      {isExportModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-medium">Export Contacts</h2>
                <button 
                  onClick={closeExportModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Export Options */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Export Options</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Export Format
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                      value={exportFormat}
                      onChange={(e) => setExportFormat(e.target.value)}
                    >
                      <option value="csv">CSV</option>
                      <option value="excel">Excel</option>
                      <option value="txt">TXT</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Export from Group
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                      value={exportGroup}
                      onChange={(e) => setExportGroup(e.target.value)}
                    >
                      <option value="all">All Contacts</option>
                      {groups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      id="include-headers"
                      type="checkbox"
                      className="h-4 w-4 text-[#FF5722] focus:ring-[#FF5722] border-gray-300 rounded"
                      checked={exportOptions.includeHeaders}
                      onChange={() => toggleExportOption('includeHeaders')}
                    />
                    <label htmlFor="include-headers" className="ml-2 block text-sm text-gray-700">
                      Include column headers
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="include-metadata"
                      type="checkbox"
                      className="h-4 w-4 text-[#FF5722] focus:ring-[#FF5722] border-gray-300 rounded"
                      checked={exportOptions.includeMetadata}
                      onChange={() => toggleExportOption('includeMetadata')}
                    />
                    <label htmlFor="include-metadata" className="ml-2 block text-sm text-gray-700">
                      Include metadata
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="include-tags"
                      type="checkbox"
                      className="h-4 w-4 text-[#FF5722] focus:ring-[#FF5722] border-gray-300 rounded"
                      checked={exportOptions.includeTags}
                      onChange={() => toggleExportOption('includeTags')}
                    />
                    <label htmlFor="include-tags" className="ml-2 block text-sm text-gray-700">
                      Include tags
                    </label>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={closeExportModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors"
                  onClick={handleExportSubmit}
                >
                  Export Contacts
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      {isEditModalOpen && editingGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-medium">Edit Group</h2>
                <button 
                  onClick={closeEditModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={editForm.name}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={editForm.description}
                    onChange={handleEditFormChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group Type
                  </label>
                  <select
                    name="type"
                    value={editForm.type}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                  >
                    <option value="standard">Standard</option>
                    <option value="vip">VIP</option>
                    <option value="newsletter">Newsletter</option>
                    <option value="event">Event</option>
                    <option value="loyalty">Loyalty Program</option>
                  </select>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div>
                      <p className="font-medium">Allow Duplicates</p>
                      <p className="text-sm text-gray-600">Allow duplicate contacts in this group</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="allowDuplicates"
                        checked={editForm.settings.allowDuplicates}
                        onChange={handleEditFormChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div>
                      <p className="font-medium">Auto-Validate</p>
                      <p className="text-sm text-gray-600">Automatically validate contact information</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="autoValidate"
                        checked={editForm.settings.autoValidate}
                        onChange={handleEditFormChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div>
                      <p className="font-medium">Require Email</p>
                      <p className="text-sm text-gray-600">Make email address mandatory</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="requireEmail"
                        checked={editForm.settings.requireEmail}
                        onChange={handleEditFormChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div>
                      <p className="font-medium">Require Phone</p>
                      <p className="text-sm text-gray-600">Make phone number mandatory</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="requirePhone"
                        checked={editForm.settings.requirePhone}
                        onChange={handleEditFormChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90 transition-colors"
                  onClick={handleEditSubmit}
                >
                  Update Group
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && groupToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-medium">Delete Group</h2>
                <button 
                  onClick={closeDeleteModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 mb-2">
                  Are you sure you want to delete the group <span className="font-medium">{groupToDelete.name}</span>?
                </p>
                <p className="text-sm text-gray-600">
                  This action cannot be undone. All contacts in this group will be removed from the group.
                </p>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  onClick={handleDeleteGroup}
                >
                  Delete Group
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactManagement; 